/**
 * Problem reports and client telemetry (docs/ONLINE.md "Problem reports"): the payload the client sends to
 * `POST /api/report` and the limits both sides enforce. The server never trusts any of it: every string is
 * clipped, stripped of control characters and stored as data; the admin page shows it with textContent only.
 */

/** `report`: a player pressed "Report a problem". The rest are automatic and carry no free text from the player. */
export const REPORT_KINDS = ['report', 'error', 'rejection', 'webgl', 'lowfps'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/** Optional category a player picks in the form. */
export const REPORT_CATEGORIES = ['bug', 'stuck', 'cars', 'online', 'money', 'performance', 'controls', 'other'] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export const REPORT_LIMITS = {
  /** What the player typed (report) or the error message (automatic). */
  message: 2000,
  /** One client log line. */
  logLine: 300,
  /** Client log lines sent with a report. */
  logLines: 60,
  /** Stack trace of an automatic error report. */
  stack: 4000,
  /** Any other string in the context (UA, mode, quality tier...). */
  field: 200,
  /** The whole request body (bytes): the router refuses anything bigger before parsing. */
  body: 48 * 1024,
} as const;

/** What the client knows about itself when it reports (all optional: offline / headless clients know less). */
export interface ReportContext {
  /** Client build revision (git short hash) and protocol version. */
  build?: string;
  protocol?: number;
  /** navigator.userAgent, clipped. */
  ua?: string;
  /** CSS px and devicePixelRatio. */
  screen?: { w: number; h: number; dpr: number };
  touch?: boolean;
  /** Graphics quality tier in use (low / medium / high) and the setting (auto / ...). */
  quality?: string;
  /** Frames per second over the last seconds: average, worst 1 % (ms frame), samples. */
  fps?: { avg: number; low: number; n: number };
  /** Where the player was and what they were doing. */
  pos?: { x: number; z: number };
  mode?: string;
  /** 'offline' | 'online' | 'reconnecting' ... */
  net?: string;
  shard?: string;
  /** Online entity id (not personal: it changes every session). */
  eid?: number;
  /** The last client log lines (console errors / warnings / info), oldest first. */
  log?: string[];
  /** Time since the page loaded (s). */
  uptimeS?: number;
}

export interface ReportPayload {
  kind: ReportKind;
  /** Free text (report) or the error's message (automatic). */
  message: string;
  category?: ReportCategory;
  /** Automatic reports: stack trace / extra detail. */
  stack?: string;
  /** Client-side de-duplication key for automatic reports (the server recomputes its own). */
  fingerprint?: string;
  /** Times this was seen on the client since the last send (automatic reports are batched). */
  count?: number;
  /** When it happened on the client (epoch ms; informative only). */
  at?: number;
  context?: ReportContext;
}
